import { useState, useEffect } from 'react';
import { tournamentGetTournaments, tournamentGetDetail } from '../services/api';
import { useGameState } from '../context/GameStateContext';

/**
 * Tournament lists for the Find screen, plus the detail of one available
 * tournament.
 *
 * The lists load whenever gameData changes and no puzzle is selected.
 *
 * @returns {{
 *   joinedTournaments: Array, availableTournaments: Array,
 *   tournamentsLoading: boolean, tournamentsError: string|null,
 *   selectedTournamentDetails: Object|null, detailsLoading: boolean,
 *   joinError: string|null,
 *   loadTournamentDetails: (tournamentKey: string) => Promise<void>,
 *   clearJoinError: () => void
 * }}
 */
export const useTournaments = () => {
  const { gameData } = useGameState();

  const [joinedTournaments, setJoinedTournaments] = useState([]);
  const [availableTournaments, setAvailableTournaments] = useState([]);
  const [tournamentsLoading, setTournamentsLoading] = useState(false);
  const [tournamentsError, setTournamentsError] = useState(null);

  const [selectedTournamentDetails, setSelectedTournamentDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [joinError, setJoinError] = useState(null);

  // Load tournament data on mount, but only if the Find screen is actually shown
  useEffect(() => {
    if (gameData?.state) {
      return;
    }
    setTournamentsLoading(true);

    tournamentGetTournaments()
      .then(data => {
        setJoinedTournaments(data.joined || []);
        setAvailableTournaments(data.available || []);
        setTournamentsError(null);
      })
      .catch(error => {
        console.error('Error fetching tournaments:', error);
        setTournamentsError('Failed to load tournaments. Please try again.');
      })
      .finally(() => {
        setTournamentsLoading(false);
      });
  }, [gameData]);

  const loadTournamentDetails = async (tournamentKey) => {
    setDetailsLoading(true);
    setSelectedTournamentDetails(null);
    setJoinError(null);

    try {
      const detailData = await tournamentGetDetail(tournamentKey);
      setSelectedTournamentDetails(detailData);
    } catch (error) {
      setJoinError('Failed to load tournament details. Please try again.');
    } finally {
      setDetailsLoading(false);
    }
  };

  const clearJoinError = () => setJoinError(null);

  return {
    joinedTournaments,
    availableTournaments,
    tournamentsLoading,
    tournamentsError,
    selectedTournamentDetails,
    detailsLoading,
    joinError,
    loadTournamentDetails,
    clearJoinError,
  };
};

export default useTournaments;
